/**
 * 侧边栏导航组件
 * 新视觉设计（参考设计稿）：白色圆角卡片 + 灰底选中态
 * 支持展开/收缩，包含所有功能入口
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DocumentDuplicateIcon,
  LinkIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  XMarkIcon,
  UsersIcon,
  BellIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { UserInfo } from '../types';
import { configApi, authApi, notificationApi, companyApi } from '../services/api';
import { getModelDisplayName, filterModels, filterVisionModels, TEXT_MODELS_CONFIG } from '../config/aiConfig';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import NotificationCenter from './NotificationCenter';

// 设计稿切图资源
import logoImg from '../assets/design/智能标书logo.png';
import foldSvg from '../assets/design/fold.svg';
import iconNewProject from '../assets/design/新建标书.svg';
import iconProjectMgmt from '../assets/design/项目管理.svg';
import iconProjectGroup from '../assets/design/项目组管理.svg';
import iconCompany from '../assets/design/主体管理.svg';
import avatarImg from '../assets/design/avatar.png';
import aiEntryImg from '../assets/design/ai-entry.png';
import { SKILL_URL, TOKEN_MANAGEMENT_URL, USER_MANAGEMENT_URL } from '../config/externalLinks';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  currentUser: UserInfo;
  onLogout: () => void;
}

// 品牌副标题
const APP_SUBTITLE = 'AI解析·智能标书';

const Sidebar: React.FC<SidebarProps> = ({
  activeView,
  onViewChange,
  currentUser,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // 根据当前路由自动设置 activeView
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === '/projects') return 'project-management';
    if (path.startsWith('/project-groups')) return 'project-group-management';
    if (path === '/create' || path.startsWith('/project/')) return 'new-project';
    if (path.startsWith('/templates')) return 'template-management';
    if (path.startsWith('/companies')) return 'company-management';
    if (path.startsWith('/resources')) return 'resource-management';
    return activeView;
  };

  const currentActiveView = getCurrentView();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [textModel, setTextModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [textModels, setTextModels] = useState<string[]>([]);
  const [visionModels, setVisionModels] = useState<string[]>([]);
  // 使用currentUser.name（优先display_name，其次username）
  const [username, setUsername] = useState(currentUser.name);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [showCompanyConfirm, setShowCompanyConfirm] = useState(false);

  // 消息通知状态
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);

  // ===== 头像悬浮菜单（标书消息 / 安装SKILL / 退出登录，参考 shouqian 侧栏） =====
  // 菜单 portal 到 body 并 fixed 定位：侧栏 overflow:hidden，展开/折叠两态都不能被裁剪
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const footRef = useRef<HTMLDivElement>(null);
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    if (!menuOpen && avatarBtnRef.current) {
      const r = avatarBtnRef.current.getBoundingClientRect();
      setMenuStyle({ left: r.left, bottom: window.innerHeight - r.top + 8 });
    }
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (footRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  /** 标书消息 → 打开消息通知中心 */
  const openMessages = () => {
    setMenuOpen(false);
    setShowNotificationCenter(true);
  };

  /** 菜单项跳转：关菜单并新 tab 打开站外系统页面 */
  const openExternalPage = (url: string) => {
    setMenuOpen(false);
    window.open(url, '_blank');
  };

  // Toast状态
  const [toast, setToast] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });

  // 加载未读消息数量（统一登录后 token 在父域 Cookie，由后端读取）
  const loadUnreadCount = async () => {
    try {
      const res = await notificationApi.getUnreadCount();
      setUnreadCount(res.data?.unread_count || 0);
    } catch (error) {
      // 静默处理错误，不打印日志避免干扰
    }
  };

  // 组件挂载时加载未读数量（不轮询，只在页面刷新或切换模块时请求）
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // 检查用户是否有主体
  useEffect(() => {
    const checkCompany = async () => {
      try {
        const response = await companyApi.getCompanies();
        setHasCompany(response.data && response.data.length > 0);
      } catch (e) {
        setHasCompany(false);
      }
    };
    checkCompany();

    // 监听主体更新事件，刷新主体状态
    const handleCompanyUpdate = () => {
      checkCompany();
    };
    window.addEventListener('company:updated', handleCompanyUpdate);

    return () => {
      window.removeEventListener('company:updated', handleCompanyUpdate);
    };
  }, []);

  // 加载可用模型列表和用户设置
  useEffect(() => {
    loadAvailableModels();
    loadUserSettings();
  }, []);

  // 监听currentUser变化，同步更新username状态
  useEffect(() => {
    setUsername(currentUser.name);
  }, [currentUser.name]);

  const loadAvailableModels = async () => {
    try {
      setLoadingModels(true);

      // 从后端获取可用模型列表（后端使用本地配置代理请求）
      const response = await configApi.getModels();

      if (response.data.success && response.data.models) {
        const models = response.data.models;
        setAvailableModels(models);

        // 过滤文本模型和视觉模型（只保留配置中的模型）
        const text = filterModels(models);
        const vision = filterVisionModels(models);

        setTextModels(text);
        setVisionModels(vision);

        // 如果没有设置过视觉模型，默认选择第一个可用的视觉模型
        if (vision.length > 0 && !visionModel) {
          setVisionModel(vision[0]);
        }
        // 如果没有设置过文本模型，默认选择第一个可用的文本模型
        if (text.length > 0 && !textModel) {
          setTextModel(text[0]);
        }
      }
    } catch (error) {
      console.error('加载模型列表失败:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  // 加载用户设置
  const loadUserSettings = async () => {
    try {
      const response = await authApi.getSettings();
      if (response.data) {
        const { text_model, vision_model, display_name } = response.data;
        if (text_model) setTextModel(text_model);
        if (vision_model) setVisionModel(vision_model);
        // 如果后端返回了display_name，使用它；否则保持当前值
        if (display_name) {
          setUsername(display_name);
        }
      }
    } catch (error) {
      console.error('加载用户设置失败:', error);
    }
  };

  // 保存用户设置
  const saveUserSettings = async () => {
    try {
      await authApi.updateSettings({
        text_model: textModel,
        vision_model: visionModel,
        display_name: username
      });

      // 更新localStorage中的用户信息
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          user.display_name = username;
          localStorage.setItem('user', JSON.stringify(user));

          // 触发用户信息更新事件，通知App.tsx刷新
          window.dispatchEvent(new CustomEvent('user:updated', {
            detail: { display_name: username }
          }));
        } catch (e) {
          console.error('Failed to update user in localStorage:', e);
        }
      }

      // 关闭弹窗
      setShowSettings(false);

      // 显示成功提示
      setToast({
        show: true,
        type: 'success',
        message: '设置保存成功'
      });

      return true;
    } catch (error) {
      console.error('保存用户设置失败:', error);

      // 显示失败提示
      setToast({
        show: true,
        type: 'error',
        message: '设置保存失败，请稍后重试'
      });

      return false;
    }
  };

  // 处理退出登录
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  // 处理新建标书点击
  const handleNewProject = () => {
    if (hasCompany === false) {
      setShowCompanyConfirm(true);
    } else {
      navigate('/create');
    }
  };

  // 导航菜单项（含角色可见性控制）
  // iconImg: 设计稿切图图标；Icon: heroicons 兜底（设计稿未提供的管理类入口）
  const allMenuItems: Array<{
    id: string;
    name: string;
    iconImg?: string;
    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    onClick: () => void;
    roles: string[];
    divider?: boolean;
  }> = [
    {
      id: 'new-project',
      name: '新建标书',
      iconImg: iconNewProject,
      onClick: handleNewProject,
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'project-management',
      name: '项目管理',
      iconImg: iconProjectMgmt,
      onClick: () => navigate('/projects'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'project-group-management',
      name: '项目组管理',
      iconImg: iconProjectGroup,
      onClick: () => navigate('/project-groups'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'company-management',
      name: '主体管理',
      iconImg: iconCompany,
      onClick: () => navigate('/companies'),
      roles: ['super', 'admin', 'user'],
    },
    {
      id: 'template-management',
      name: '模板管理',
      Icon: DocumentDuplicateIcon,
      onClick: () => navigate('/templates'),
      roles: ['super'],  // 仅超级管理员
    },
    {
      id: 'resource-management',
      name: '资源管理',
      Icon: LinkIcon,
      onClick: () => navigate('/resources'),
      roles: ['super'],  // 仅超级管理员
    },
  ];
  const menuItems = allMenuItems.filter(item => item.roles.includes(currentUser.role));
  // 标记过滤后第一个带 divider 的菜单项（避免普通用户 Token 前重复显示分割线）
  const firstDividerId = menuItems.find(item => item.divider)?.id;

  // 角色显示文案
  const roleLabel = currentUser.role === 'super' ? '超级管理员' : currentUser.role === 'admin' ? '管理员' : '普通用户';

  // 文字淡入：展开时延迟150ms后淡入，收起时立即隐藏
  const textClass = isExpanded
    ? 'opacity-100 transition-opacity duration-150 delay-150'
    : 'opacity-0 transition-opacity duration-75';

  return (
    <div
      className={`flex flex-col h-full flex-shrink-0 bg-white rounded-[14px] overflow-hidden ${
        isExpanded ? 'w-[226px] p-4' : 'w-[64px] px-2 py-4 items-center'
      }`}
      style={{
        boxShadow: '0 2px 12px rgba(31,41,55,.05)',
        transition: 'width .28s cubic-bezier(.22,.8,.3,1), padding .28s cubic-bezier(.22,.8,.3,1)',
      }}
    >
      {/* 品牌区 */}
      {isExpanded ? (
        <div className="flex items-center gap-3 min-h-[40px] flex-shrink-0">
          <img src={logoImg} alt="智能标书" className="w-10 h-10 rounded-[10px] flex-shrink-0" />
          <div className="min-w-0 whitespace-nowrap">
            <h1 className="text-xl font-bold tracking-wide leading-tight text-[#26292C]">智能标书</h1>
            <p className="text-xs text-[#575C62] tracking-wide leading-tight">{APP_SUBTITLE}</p>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#EEEFF2] transition-colors flex-shrink-0"
            title="折叠"
          >
            <img src={foldSvg} alt="折叠" className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex-shrink-0"
          title="展开侧栏"
        >
          <img src={logoImg} alt="智能标书" className="w-10 h-10 rounded-[10px]" />
        </button>
      )}

      {/* 导航菜单 */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden hide-scrollbar flex flex-col ${
        isExpanded ? 'mt-5 gap-1' : 'mt-8 gap-2 items-center'
      }`}>
        {menuItems.map((item) => {
          const isActive = currentActiveView === item.id;
          const showDivider = item.id === firstDividerId;
          const ItemIcon = item.Icon;

          return (
            <React.Fragment key={item.id}>
              {/* 系统管理分组分隔 */}
              {showDivider && (
                isExpanded ? (
                  <div className="flex items-center gap-2.5 mx-1.5 mt-3.5 mb-2.5 whitespace-nowrap flex-shrink-0">
                    <div className="flex-1 h-px bg-black/[.08]" />
                    <span className={`text-xs text-[#9AA0A6] tracking-widest ${textClass}`}>系统管理</span>
                    <div className="flex-1 h-px bg-black/[.08]" />
                  </div>
                ) : (
                  <div className="w-6 h-px bg-black/[.08] my-3.5 flex-shrink-0" />
                )
              )}
              <button
                onClick={item.onClick}
                title={!isExpanded ? item.name : undefined}
                className={`flex items-center flex-shrink-0 rounded-[10px] text-[14px] whitespace-nowrap transition-colors ${
                  isExpanded
                    ? 'w-full h-11 px-3 gap-3'
                    : 'w-11 h-11 justify-center rounded-xl'
                } ${
                  isActive
                    ? 'bg-[#EEEFF2] font-semibold text-[#26292C]'
                    : 'text-[#26292C] hover:bg-[#EEEFF2]'
                }`}
              >
                {item.iconImg ? (
                  <img src={item.iconImg} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
                ) : (
                  ItemIcon && <ItemIcon className="w-5 h-5 flex-shrink-0" />
                )}
                {isExpanded && (
                  <span className={textClass}>{item.name}</span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* 底部用户区（参考 shouqian 侧栏：头像+姓名+版本+AI入口，点头像弹账户菜单） */}
      {isExpanded ? (
        <div ref={footRef} className="mt-auto flex items-center gap-3 pt-4 flex-shrink-0 whitespace-nowrap">
          <button
            ref={avatarBtnRef}
            onClick={toggleMenu}
            className="rounded-full flex-shrink-0 transition-shadow hover:shadow-[0_0_0_3px_rgba(0,0,0,.06)]"
            title="账户菜单"
          >
            <img src={avatarImg} alt={currentUser.name} className="w-10 h-10 rounded-full block" />
          </button>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[17px] text-[#26292C] leading-[1.3] truncate">{currentUser.name}</span>
            <span className="text-[10px] text-[#575C62] tracking-[0.5px] leading-[1.3]">V2.0工作台</span>
          </div>
          <button
            onClick={() => setToast({ show: true, type: 'info', message: 'AI助手功能即将上线，敬请期待' })}
            className="ml-auto mr-1.5 w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 transition-transform hover:scale-[1.08]"
            title="AI助手"
          >
            <img src={aiEntryImg} alt="AI助手" className="w-8 h-8 rounded-lg" />
          </button>
        </div>
      ) : (
        <div ref={footRef} className="mt-auto flex flex-col items-center gap-4 flex-shrink-0">
          <button
            onClick={() => setToast({ show: true, type: 'info', message: 'AI助手功能即将上线，敬请期待' })}
            className="w-9 h-9 rounded-[9px] flex items-center justify-center transition-transform hover:scale-[1.08]"
            title="AI助手"
          >
            <img src={aiEntryImg} alt="AI助手" className="w-8 h-8 rounded-lg" />
          </button>
          <button
            ref={avatarBtnRef}
            onClick={toggleMenu}
            className="rounded-full transition-shadow hover:shadow-[0_0_0_3px_rgba(0,0,0,.06)]"
            title="账户菜单"
          >
            <img src={avatarImg} alt={currentUser.name} className="w-9 h-9 rounded-full block" />
          </button>
        </div>
      )}

      {/* 头像悬浮菜单：标书消息 / 安装SKILL / 退出登录（portal 到 body，fixed 定位） */}
      {menuOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[1000] min-w-[148px] p-1.5 bg-white rounded-xl flex flex-col gap-0.5"
          style={{ ...menuStyle, boxShadow: '0 8px 30px rgba(31,41,55,.14), 0 0 0 1px rgba(0,0,0,.04)' }}
        >
          <button
            onClick={openMessages}
            className="flex items-center gap-2.5 h-[38px] px-3 rounded-lg text-[13px] text-[#26292C] hover:bg-black/[.06] transition-colors whitespace-nowrap"
          >
            <BellIcon className="w-4 h-4 flex-shrink-0" />
            标书消息
            {unreadCount > 0 && (
              <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] leading-[18px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => openExternalPage(SKILL_URL)}
            className="flex items-center gap-2.5 h-[38px] px-3 rounded-lg text-[13px] text-[#26292C] hover:bg-black/[.06] transition-colors whitespace-nowrap"
          >
            <ArrowDownTrayIcon className="w-4 h-4 flex-shrink-0" />
            安装SKILL
          </button>
          <button
            onClick={() => openExternalPage(TOKEN_MANAGEMENT_URL)}
            className="flex items-center gap-2.5 h-[38px] px-3 rounded-lg text-[13px] text-[#26292C] hover:bg-black/[.06] transition-colors whitespace-nowrap"
          >
            <CircleStackIcon className="w-4 h-4 flex-shrink-0" />
            Token管理
          </button>
          {(currentUser.role === 'super' || currentUser.role === 'admin') && (
            <button
              onClick={() => openExternalPage(USER_MANAGEMENT_URL)}
              className="flex items-center gap-2.5 h-[38px] px-3 rounded-lg text-[13px] text-[#26292C] hover:bg-black/[.06] transition-colors whitespace-nowrap"
            >
              <UsersIcon className="w-4 h-4 flex-shrink-0" />
              人员管理
            </button>
          )}
          <button
            onClick={() => { setMenuOpen(false); handleLogout(); }}
            className="flex items-center gap-2.5 h-[38px] px-3 rounded-lg text-[13px] text-red-600 hover:bg-black/[.06] transition-colors whitespace-nowrap"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
            退出登录
          </button>
        </div>,
        document.body
      )}

      {/* 系统设置弹窗 */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">系统设置</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 弹窗内容 */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* 模型设置 */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                  模型设置
                </h3>

                {/* 文本模型 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    文本模型（文件解析和内容生成）
                  </label>
                  <select
                    value={textModel}
                    onChange={(e) => setTextModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loadingModels || textModels.length === 0}
                  >
                    {TEXT_MODELS_CONFIG.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} ({m.company})</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {loadingModels ? '正在获取模型列表...' : `共 ${textModels.length} 个文本模型`}
                  </p>
                </div>

                {/* 视觉模型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    视觉模型（图像识别和生成）
                  </label>
                  <select
                    value={visionModel}
                    onChange={(e) => setVisionModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loadingModels || visionModels.length === 0}
                  >
                    {visionModels.length > 0 ? (
                      visionModels.map((model) => (
                        <option key={model} value={model}>{getModelDisplayName(model)}</option>
                      ))
                    ) : (
                      <option value="">{loadingModels ? '加载中...' : '暂无可用模型'}</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {loadingModels ? '正在获取模型列表...' : `共 ${visionModels.length} 个视觉模型`}
                  </p>
                </div>
              </div>

              {/* 分割线 */}
              <div className="border-t border-gray-200 my-6"></div>

              {/* 用户设置 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                  用户设置
                </h3>

                {/* 显示账号 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    账号
                  </label>
                  <input
                    type="text"
                    value={currentUser.username}
                    disabled
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* 修改用户名 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    用户名
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                {/* 角色显示 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    角色
                  </label>
                  <input
                    type="text"
                    value={roleLabel}
                    disabled
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* 退出登录按钮 */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  退出登录
                </button>
              </div>
            </div>

            {/* 弹窗底部 */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveUserSettings}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 退出登录确认对话框 */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="确认退出登录？"
        message="您确定要退出登录吗？退出后将返回到登录页面。"
        confirmText="确认退出"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      {/* Toast通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.message}
        duration={3000}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* 消息通知中心 */}
      <NotificationCenter
        isOpen={showNotificationCenter}
        onClose={() => {
          setShowNotificationCenter(false);
          loadUnreadCount(); // 关闭时刷新未读数量
        }}
      />

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={showCompanyConfirm}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setShowCompanyConfirm(false);
          navigate('/companies/new');
        }}
        onCancel={() => {
          setShowCompanyConfirm(false);
          navigate('/create');
        }}
      />
    </div>
  );
};

export default Sidebar;
